import { useMemo, Fragment, ReactNode } from 'react';

import { FormFieldType } from 'types';
import { isGradedSelect } from 'utils/choice-response';
import { isStaticFormField } from 'utils/static-form-field';

import { FormText } from './text';
import { FormHeading } from './heading';
import { FormInputField } from './input';
import { FormRadioField } from './radio';
import { FormSelectField } from './select';
import { FormCheckboxField } from './checkbox';
import { FieldArrayField } from './field-array';
import { DynamicFormFieldsProps } from './types';
import { FormCsvLookupField } from './csv-lookup';
import { FormScaleRatingField } from './scale-rating';
import { FormSelectCorrectField } from './select-correct';

export const DynamicFormFields = ({
  formId,
  fields,
  isSaved,
  container,
  hideLabels,
  staticContainer,
}: DynamicFormFieldsProps) => {
  const items = useMemo(() => {
    // Static content bypasses question wrappers, keeping their indexes contiguous.
    let wrappedFieldIndex = 0;
    return fields.map((field, index) => {
      let fieldNode: ReactNode;
      const props = { ...field };
      if (hideLabels && !isStaticFormField(props.type)) {
        props.label = '';
      }
      switch (props.type) {
        case FormFieldType.INPUT:
          fieldNode = <FormInputField key={props.id} {...props} />;
          break;
        case FormFieldType.SELECT:
          fieldNode = isGradedSelect(props) ? (
            <FormSelectCorrectField
              key={props.id}
              {...props}
              disabled={isSaved ? isSaved(props.id) : false}
            />
          ) : (
            <FormSelectField key={props.id} {...props} />
          );
          break;
        case FormFieldType.RADIO:
          fieldNode = <FormRadioField key={props.id} {...props} />;
          break;
        case FormFieldType.CHECKBOX:
          fieldNode = <FormCheckboxField key={props.id} {...props} />;
          break;
        case FormFieldType.SCALE_RATING:
          fieldNode = <FormScaleRatingField key={props.id} {...props} />;
          break;
        case FormFieldType.FIELD_ARRAY:
          fieldNode = (
            <FieldArrayField key={props.id} formId={formId} {...props} />
          );
          break;
        case FormFieldType.TEXT:
          fieldNode = <FormText key={props.id} {...props} />;
          break;
        case FormFieldType.HEADING:
          fieldNode = <FormHeading key={props.id} {...props} />;
          break;
        case FormFieldType.CSV_LOOKUP:
          fieldNode = (
            <FormCsvLookupField key={props.id} formId={formId} {...props} />
          );
          break;
        default:
          fieldNode = <Fragment key={index} />;
          break;
      }
      if (isStaticFormField(field.type)) {
        return staticContainer ? staticContainer(field, fieldNode) : fieldNode;
      }

      const containerIndex = wrappedFieldIndex;
      wrappedFieldIndex += 1;
      return container
        ? container(field, fieldNode, containerIndex)
        : fieldNode;
    });
  }, [fields, container, staticContainer, formId, isSaved, hideLabels]);

  return <>{items}</>;
};
